// ==================== WHATSAPP SETTINGS PAGE ====================

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Smartphone, 
  Key,
  Shield,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Save,
  Eye,
  EyeOff,
  ExternalLink,
  Copy,
  Check,
  Link2,
  AlertTriangle,
  Unplug,
  Zap,
  Plus,
} from 'lucide-react';
import { Button, Input, Badge, ConfirmDialog } from '../../../components/ui';
import { AddOrganizationModal, BuyOrganizationsModal } from '../../../components/organizations';
import { apiService } from '../../../services/api';
import type { Organization } from '../../../services/organizations.service';
import { useOrganizations } from '../../../hooks/useOrganizations';
import type { ApiResponse } from '../../../types';

// ==================== TYPES ====================

interface WhatsAppAccount {
  id: string;
  metaAppId: string;
  phoneNumberId: string;
  displayPhoneNumber: string;
  businessAccountId: string;
  webhookVerifyToken: string;
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'DISCONNECTED';
  createdAt: string;
  updatedAt: string;
}

// ==================== VALIDATION SCHEMA ====================

// Dynamic schema that validates secrets only for new accounts
const createWhatsappSchema = (isEditing: boolean) => z.object({
  metaAppId: z.string().min(1, 'Meta App ID is required'),
  metaAppSecret: isEditing 
    ? z.string() // Allow empty when editing (keeps existing value)
    : z.string().min(1, 'Meta App Secret is required'),
  phoneNumberId: z.string().min(1, 'Phone Number ID is required').regex(/^\d+$/, 'Phone Number ID must contain only numbers'),
  displayPhoneNumber: z.string().min(1, 'Display Phone Number is required'),
  businessAccountId: z.string().min(1, 'Business Account ID is required').regex(/^\d+$/, 'Business Account ID must contain only numbers'),
  accessToken: isEditing 
    ? z.string() // Allow empty when editing (keeps existing value)
    : z.string().min(1, 'Access Token is required'),
});

type WhatsAppFormData = z.infer<ReturnType<typeof createWhatsappSchema>>;

// ==================== STATUS BADGE COMPONENT ====================

const StatusBadge = ({ status }: { status: WhatsAppAccount['status'] }) => {
  const config = {
    ACTIVE: { variant: 'success' as const, label: 'Connected', icon: CheckCircle },
    PENDING: { variant: 'warning' as const, label: 'Pending', icon: AlertCircle },
    SUSPENDED: { variant: 'error' as const, label: 'Suspended', icon: AlertTriangle },
    DISCONNECTED: { variant: 'default' as const, label: 'Disconnected', icon: Unplug },
  };
  
  const { variant, label, icon: Icon } = config[status];
  
  return (
    <Badge variant={variant} className="flex items-center gap-1">
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

// ==================== COPY BUTTON COMPONENT ====================

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <button
      onClick={handleCopy}
      className="p-1.5 rounded-md hover:bg-gray-700 transition-colors text-gray-400 hover:text-white"
      title="Copy to clipboard"
    >
      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
    </button>
  );
};

// ==================== MAIN COMPONENT ====================

interface TestConnectionResult {
  success: boolean;
  message: string;
  data?: {
    verified_name?: string;
    display_phone_number?: string;
    quality_rating?: string;
  };
}

// Live account status from Meta
interface MetaAccountStatus {
  success: boolean;
  message?: string;
  phoneNumber?: {
    verifiedName: string;
    displayPhoneNumber: string;
    qualityRating: string;
    qualityRatingColor: 'GREEN' | 'YELLOW' | 'RED' | 'UNKNOWN';
    codeVerificationStatus: string;
    nameStatus: string;
    messagingLimitTier: string;
    status: string;
    platformType: string;
    throughput?: {
      level: string;
    };
  };
  businessAccount?: {
    name: string;
    accountReviewStatus: string;
    timezoneId: string;
    ownerBusinessId?: string;
  };
}

const WhatsAppSettingsPage = () => {
  // Multiple organizations: this page manages the WhatsApp number of the account chosen in the
  // header (requests then carry no organizationId for a single account, as before)
  const { purchased, created, isMultiOrganization, refetch, selectedOrganizationId, selectOrganization, requireAccount } =
    useOrganizations();
  const needsAccount = isMultiOrganization && !selectedOrganizationId;
  const organizationQuery = selectedOrganizationId ? `?organizationId=${selectedOrganizationId}` : '';
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [account, setAccount] = useState<WhatsAppAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [metaStatus, setMetaStatus] = useState<MetaAccountStatus | null>(null);
  const [loadingMetaStatus, setLoadingMetaStatus] = useState(false);

  // Determine if we're editing an existing account (secrets are optional)
  const isEditingExisting = Boolean(account);

  // Dynamic schema based on whether we're editing or creating
  const formSchema = useMemo(() => createWhatsappSchema(isEditingExisting), [isEditingExisting]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<WhatsAppFormData>({
    resolver: zodResolver(formSchema),
  });

  // Fetch WhatsApp account
  const fetchAccount = useCallback(async () => {
    if (needsAccount) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.get<WhatsAppAccount>(`/customer/whatsapp${organizationQuery}`);
      if (response.success && response.data) {
        setAccount(response.data);
        reset({
          metaAppId: response.data.metaAppId,
          metaAppSecret: '', // Don't prefill secrets
          phoneNumberId: response.data.phoneNumberId,
          displayPhoneNumber: response.data.displayPhoneNumber,
          businessAccountId: response.data.businessAccountId,
          accessToken: '', // Don't prefill secrets
        });
      }
    } catch {
      // No account exists yet
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, [reset, needsAccount, organizationQuery]);

  // Fetch live status from Meta
  const fetchMetaStatus = useCallback(async () => {
    if (!account) return;
    
    try {
      setLoadingMetaStatus(true);
      const response = await apiService.get<MetaAccountStatus>(`/customer/whatsapp/status${organizationQuery}`);
      // The backend returns the status object directly with success property
      const statusData = response as unknown as MetaAccountStatus;
      if (statusData.success) {
        setMetaStatus(statusData);
      } else {
        setMetaStatus({ success: false, message: statusData.message || 'Failed to fetch status' });
      }
    } catch {
      setMetaStatus({ success: false, message: 'Failed to fetch account status from Meta' });
    } finally {
      setLoadingMetaStatus(false);
    }
  }, [account, organizationQuery]);

  // Multiple organizations: showing another account starts from scratch
  const shownOrganizationIdRef = useRef(selectedOrganizationId);
  useEffect(() => {
    if (shownOrganizationIdRef.current === selectedOrganizationId) return;
    shownOrganizationIdRef.current = selectedOrganizationId;
    setAccount(null);
    setMetaStatus(null);
    setTestResult(null);
    setIsEditing(false);
    reset({
      metaAppId: '',
      metaAppSecret: '',
      phoneNumberId: '',
      displayPhoneNumber: '',
      businessAccountId: '',
      accessToken: '',
    });
  }, [selectedOrganizationId, reset]);

  // Multiple organizations: this page needs a specific account
  useEffect(() => {
    if (needsAccount) requireAccount('connect WhatsApp');
  }, [needsAccount, requireAccount]);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  // Fetch Meta status when account is loaded
  useEffect(() => {
    if (account && account.status !== 'DISCONNECTED') {
      fetchMetaStatus();
    }
  }, [account, fetchMetaStatus]);

  const showSuccessMessage = (message: string) => {
    setSuccess(message);
    setError(null);
    setTimeout(() => setSuccess(null), 3000);
  };

  const showErrorMessage = (message: string) => {
    setError(message);
    setSuccess(null);
  };

  // Handle form submit
  const onSubmit = async (data: WhatsAppFormData) => {
    try {
      setSaving(true);
      setError(null);

      let response: ApiResponse<WhatsAppAccount>;
      
      if (account) {
        // Update existing account
        response = await apiService.patch<WhatsAppAccount>(`/customer/whatsapp${organizationQuery}`, data);
      } else {
        // Create new account
        response = await apiService.post<WhatsAppAccount>(`/customer/whatsapp${organizationQuery}`, data);
      }

      if (response.success && response.data) {
        setAccount(response.data);
        setIsEditing(false);
        if (account) {
          // Updated existing account
          showSuccessMessage('WhatsApp settings updated successfully!');
        } else {
          // Created new account - pending verification
          showSuccessMessage('WhatsApp account saved! Please test the connection or configure the webhook to activate.');
        }
      } else {
        showErrorMessage(response.message || 'Failed to save WhatsApp settings');
      }
    } catch {
      showErrorMessage('Failed to save WhatsApp settings');
    } finally {
      setSaving(false);
    }
  };

  // Handle disconnect confirmation
  const handleDisconnectConfirm = async () => {
    try {
      setIsDisconnecting(true);
      const response = await apiService.delete(`/customer/whatsapp${organizationQuery}`);
      if (response.success) {
        setAccount(null);
        reset({
          metaAppId: '',
          metaAppSecret: '',
          phoneNumberId: '',
          displayPhoneNumber: '',
          businessAccountId: '',
          accessToken: '',
        });
        setShowDisconnectDialog(false);
        showSuccessMessage('WhatsApp account disconnected');
      } else {
        showErrorMessage(response.message || 'Failed to disconnect');
      }
    } catch {
      showErrorMessage('Failed to disconnect WhatsApp account');
    } finally {
      setIsDisconnecting(false);
    }
  };

  // Test connection
  const handleTestConnection = async () => {
    try {
      setIsTesting(true);
      setTestResult(null);
      setError(null);
      
      // The backend returns TestConnectionResult directly (with success, message, data)
      // The TransformResponseInterceptor passes it through as-is since it has 'success' property
      // So the response object IS the TestConnectionResult
      const response = await apiService.post<TestConnectionResult>(`/webhook/whatsapp/test${organizationQuery}`);
      
      // Cast to access the actual structure since backend returns TestConnectionResult directly
      const result = response as unknown as TestConnectionResult;
      
      if (result.success) {
        setTestResult({
          success: result.success,
          message: result.message,
          data: result.data,
        });
        // Refresh account to get updated status
        await fetchAccount();
      } else {
        setTestResult({
          success: false,
          message: result.message || 'Connection test failed',
        });
      }
    } catch {
      setTestResult({
        success: false,
        message: 'Failed to test connection. Please try again.',
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Add Account: within the number purchased, else buy more first
  const handleAddAccount = () => {
    setError(null);
    setSuccess(null);
    if (created < purchased) {
      setShowAddModal(true);
    } else {
      setShowBuyModal(true);
    }
  };

  const handlePurchased = async () => {
    setShowBuyModal(false);
    await refetch();
    setShowAddModal(true);
  };

  const handleAccountCreated = async (organization: Organization) => {
    setShowAddModal(false);
    await refetch();
    selectOrganization(organization.id);
    setSuccess('WhatsApp account added. Connect its WhatsApp number below.');
  };

  const pageHeader = (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="text-base font-heading font-semibold text-white">WhatsApp Integration</h2>
        <p className="text-xs text-(--color-muted-gray) mt-0.5">
          Connect your WhatsApp Business account to send messages
        </p>
      </div>
      <Button size="sm" onClick={handleAddAccount} leftIcon={<Plus className="w-4 h-4" />}>
        Add Account
      </Button>
    </div>
  );

  const addAccountModals = (
    <>
      <AddOrganizationModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreated={handleAccountCreated}
      />
      <BuyOrganizationsModal
        isOpen={showBuyModal}
        onClose={() => setShowBuyModal(false)}
        onPurchased={handlePurchased}
      />
    </>
  );

  // Multiple organizations: with "All Accounts" chosen there is no WhatsApp number to manage
  // (the notice under the header asks for a specific account)
  if (needsAccount) {
    return (
      <div className="space-y-4">
        {pageHeader}
        {addAccountModals}
      </div>
    );
  }

  // Generate webhook URL
  const webhookUrl = `${import.meta.env.VITE_API_BASE_URL + '/' + import.meta.env.VITE_API_PREFIX + '/' + import.meta.env.VITE_API_VERSION}/webhook/whatsapp`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex flex-col items-center gap-2">
          <RefreshCw className="w-6 h-6 text-orange-500 animate-spin" />
          <p className="text-sm text-(--color-muted-gray)">Loading WhatsApp settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      {pageHeader}

      {/* Success/Error Messages */}
      <AnimatePresence>
        {success && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <p className="text-sm text-emerald-400">{success}</p>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300 text-xs">
              Dismiss
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Connection Status Card */}
      {account && !isEditing && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#121723] rounded-xl border border-gray-800"
        >
          <div className="p-4 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  account.status === 'ACTIVE' ? 'bg-emerald-500/20' :
                  account.status === 'PENDING' ? 'bg-yellow-500/20' :
                  account.status === 'SUSPENDED' ? 'bg-red-500/20' :
                  'bg-gray-500/20'
                }`}>
                  <Smartphone className={`w-5 h-5 ${
                    account.status === 'ACTIVE' ? 'text-emerald-400' :
                    account.status === 'PENDING' ? 'text-yellow-400' :
                    account.status === 'SUSPENDED' ? 'text-red-400' :
                    'text-gray-400'
                  }`} />
                </div>
                <div>
                  <h3 className="text-sm font-heading font-semibold text-white">
                    {account.status === 'ACTIVE' ? 'WhatsApp Connected' :
                     account.status === 'PENDING' ? 'WhatsApp Pending Verification' :
                     account.status === 'SUSPENDED' ? 'WhatsApp Suspended' :
                     'WhatsApp Disconnected'}
                  </h3>
                  <p className="text-xs text-(--color-muted-gray)">{account.displayPhoneNumber}</p>
                </div>
              </div>
              <StatusBadge status={account.status} />
            </div>
          </div>

          <div className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                <p className="text-xs text-(--color-muted-gray) mb-0.5">Phone Number ID</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-white font-mono truncate">{account.phoneNumberId}</p>
                  <CopyButton text={account.phoneNumberId} />
                </div>
              </div>
              <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                <p className="text-xs text-(--color-muted-gray) mb-0.5">Business Account ID</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-white font-mono truncate">{account.businessAccountId}</p>
                  <CopyButton text={account.businessAccountId} />
                </div>
              </div>
              <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                <p className="text-xs text-(--color-muted-gray) mb-0.5">Meta App ID</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-white font-mono truncate">{account.metaAppId}</p>
                  <CopyButton text={account.metaAppId} />
                </div>
              </div>
              <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                <p className="text-xs text-(--color-muted-gray) mb-0.5">Webhook Verify Token</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-white font-mono truncate min-w-0 flex-1">
                    {showSecrets ? account.webhookVerifyToken : '••••••••••••'}
                  </p>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => setShowSecrets(!showSecrets)}
                      className="p-1.5 rounded-md hover:bg-gray-700 transition-colors text-gray-400 hover:text-white"
                    >
                      {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <CopyButton text={account.webhookVerifyToken} />
                  </div>
                </div>
              </div>
            </div>

            {/* Test Connection Result */}
            <AnimatePresence>
              {testResult && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className={`p-3 rounded-lg border ${
                    testResult.success 
                      ? 'bg-emerald-500/10 border-emerald-500/30' 
                      : 'bg-red-500/10 border-red-500/30'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {testResult.success ? (
                      <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${testResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                        {testResult.message}
                      </p>
                      {testResult.success && testResult.data && (
                        <div className="mt-2 space-y-1">
                          {testResult.data.verified_name && (
                            <p className="text-xs text-gray-400">
                              <span className="text-gray-500">Verified Name:</span> {testResult.data.verified_name}
                            </p>
                          )}
                          {testResult.data.display_phone_number && (
                            <p className="text-xs text-gray-400">
                              <span className="text-gray-500">Phone Number:</span> +{testResult.data.display_phone_number}
                            </p>
                          )}
                          {testResult.data.quality_rating && (
                            <p className="text-xs text-gray-400">
                              <span className="text-gray-500">Quality Rating:</span> {testResult.data.quality_rating}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <button 
                      onClick={() => setTestResult(null)}
                      className="text-gray-500 hover:text-gray-300 shrink-0"
                    >
                      ×
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center gap-2 pt-2">
              <Button 
                variant="primary" 
                size="sm" 
                onClick={handleTestConnection}
                disabled={isTesting}
              >
                {isTesting ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Zap className="w-3.5 h-3.5 mr-1.5" />
                    Test Connection
                  </>
                )}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setIsEditing(true)}>
                Edit Settings
              </Button>
              {account.status !== 'DISCONNECTED' && (
                <Button variant="ghost" size="sm" onClick={() => setShowDisconnectDialog(true)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                  <Unplug className="w-3.5 h-3.5 mr-1.5" />
                  Disconnect
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Live Meta Account Status */}
      {account && !isEditing && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-[#121723] rounded-xl border border-gray-800"
        >
          <div className="p-4 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-sm font-heading font-semibold text-white">Live Status from Meta</h3>
                  <p className="text-xs text-(--color-muted-gray)">Real-time account status from WhatsApp Business API</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchMetaStatus}
                disabled={loadingMetaStatus}
                className="text-gray-400 hover:text-white"
              >
                <RefreshCw className={`w-4 h-4 ${loadingMetaStatus ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          <div className="p-4">
            {loadingMetaStatus && !metaStatus && (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 text-orange-500 animate-spin mr-2" />
                <span className="text-sm text-(--color-muted-gray)">Fetching status from Meta...</span>
              </div>
            )}

            {metaStatus && !metaStatus.success && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                <p className="text-sm text-red-400">{metaStatus.message}</p>
              </div>
            )}

            {metaStatus && metaStatus.success && (
              <div className="space-y-4">
                {/* Phone Number Status */}
                {metaStatus.phoneNumber && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-300">
                      <Smartphone className="w-4 h-4 text-blue-400" />
                      <span className="font-medium">Phone Number Status</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                        <p className="text-xs text-(--color-muted-gray) mb-1">Verified Name</p>
                        <p className="text-sm text-white font-medium truncate" title={metaStatus.phoneNumber.verifiedName}>
                          {metaStatus.phoneNumber.verifiedName || '—'}
                        </p>
                      </div>
                      <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                        <p className="text-xs text-(--color-muted-gray) mb-1">Quality Rating</p>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            metaStatus.phoneNumber.qualityRatingColor === 'GREEN' ? 'bg-emerald-400' :
                            metaStatus.phoneNumber.qualityRatingColor === 'YELLOW' ? 'bg-yellow-400' :
                            metaStatus.phoneNumber.qualityRatingColor === 'RED' ? 'bg-red-400' :
                            'bg-gray-400'
                          }`} />
                          <p className={`text-sm font-medium ${
                            metaStatus.phoneNumber.qualityRatingColor === 'GREEN' ? 'text-emerald-400' :
                            metaStatus.phoneNumber.qualityRatingColor === 'YELLOW' ? 'text-yellow-400' :
                            metaStatus.phoneNumber.qualityRatingColor === 'RED' ? 'text-red-400' :
                            'text-gray-400'
                          }`}>
                            {metaStatus.phoneNumber.qualityRating || 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                        <p className="text-xs text-(--color-muted-gray) mb-1">Status</p>
                        <Badge 
                          variant={metaStatus.phoneNumber.status === 'CONNECTED' ? 'success' : 
                                   metaStatus.phoneNumber.status === 'DISCONNECTED' ? 'error' : 'warning'}
                          className="text-xs"
                        >
                          {metaStatus.phoneNumber.status || 'Unknown'}
                        </Badge>
                      </div>
                      <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                        <p className="text-xs text-(--color-muted-gray) mb-1">Name Status</p>
                        <p className="text-sm text-white">
                          {metaStatus.phoneNumber.nameStatus?.replace(/_/g, ' ') || '—'}
                        </p>
                      </div>
                      <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                        <p className="text-xs text-(--color-muted-gray) mb-1">Messaging Limit</p>
                        <p className="text-sm text-white">
                          {metaStatus.phoneNumber.messagingLimitTier?.replace(/_/g, ' ') || '—'}
                        </p>
                      </div>
                      <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                        <p className="text-xs text-(--color-muted-gray) mb-1">Verification</p>
                        <Badge 
                          variant={metaStatus.phoneNumber.codeVerificationStatus === 'VERIFIED' ? 'success' : 'warning'}
                          className="text-xs"
                        >
                          {metaStatus.phoneNumber.codeVerificationStatus?.replace(/_/g, ' ') || 'Unknown'}
                        </Badge>
                      </div>
                      <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                        <p className="text-xs text-(--color-muted-gray) mb-1">Platform</p>
                        <p className="text-sm text-white">
                          {metaStatus.phoneNumber.platformType?.replace(/_/g, ' ') || '—'}
                        </p>
                      </div>
                      {metaStatus.phoneNumber.throughput && (
                        <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                          <p className="text-xs text-(--color-muted-gray) mb-1">Throughput</p>
                          <p className="text-sm text-white">
                            {metaStatus.phoneNumber.throughput.level?.replace(/_/g, ' ') || '—'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Business Account Status */}
                {metaStatus.businessAccount && (
                  <div className="space-y-3 pt-3 border-t border-gray-800">
                    <div className="flex items-center gap-2 text-sm text-gray-300">
                      <Shield className="w-4 h-4 text-blue-400" />
                      <span className="font-medium">Business Account Status</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                        <p className="text-xs text-(--color-muted-gray) mb-1">Account Name</p>
                        <p className="text-sm text-white font-medium truncate" title={metaStatus.businessAccount.name}>
                          {metaStatus.businessAccount.name || '—'}
                        </p>
                      </div>
                      <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                        <p className="text-xs text-(--color-muted-gray) mb-1">Review Status</p>
                        <Badge 
                          variant={metaStatus.businessAccount.accountReviewStatus === 'APPROVED' ? 'success' : 
                                   metaStatus.businessAccount.accountReviewStatus === 'PENDING' ? 'warning' : 'default'}
                          className="text-xs"
                        >
                          {metaStatus.businessAccount.accountReviewStatus?.replace(/_/g, ' ') || 'Unknown'}
                        </Badge>
                      </div>
                      <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                        <p className="text-xs text-(--color-muted-gray) mb-1">Timezone</p>
                        <p className="text-sm text-white">
                          {metaStatus.businessAccount.timezoneId || '—'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Setup Form */}
      {(!account || isEditing) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#121723] rounded-xl border border-gray-800"
        >
          <div className="p-4 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h3 className="text-sm font-heading font-semibold text-white">
                  {account ? 'Update WhatsApp Settings' : 'Connect WhatsApp Business'}
                </h3>
                <p className="text-xs text-(--color-muted-gray)">
                  Enter your Meta App and WhatsApp Business API credentials
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
            {/* Meta App Credentials */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Key className="w-4 h-4 text-orange-400" />
                <span className="font-medium">Meta App Credentials</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1.5">Meta App ID</label>
                  <Input
                    {...register('metaAppId')}
                    placeholder="Enter Meta App ID"
                    error={errors.metaAppId?.message}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1.5">
                    Meta App Secret
                    {account && <span className="text-gray-500 font-normal ml-1">(optional)</span>}
                  </label>
                  <Input
                    type="password"
                    {...register('metaAppSecret')}
                    placeholder={account ? 'Leave empty to keep existing' : 'Enter Meta App Secret'}
                    error={errors.metaAppSecret?.message}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>

            {/* WhatsApp Business Credentials */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Smartphone className="w-4 h-4 text-orange-400" />
                <span className="font-medium">WhatsApp Business Details</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1.5">Phone Number ID</label>
                  <Input
                    {...register('phoneNumberId')}
                    placeholder="Enter Phone Number ID"
                    error={errors.phoneNumberId?.message}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1.5">Display Phone Number</label>
                  <Input
                    {...register('displayPhoneNumber')}
                    placeholder="+1 234 567 8900"
                    error={errors.displayPhoneNumber?.message}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1.5">Business Account ID</label>
                  <Input
                    {...register('businessAccountId')}
                    placeholder="Enter Business Account ID"
                    error={errors.businessAccountId?.message}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1.5">
                    Access Token
                    {account && <span className="text-gray-500 font-normal ml-1">(optional)</span>}
                  </label>
                  <Input
                    type="password"
                    {...register('accessToken')}
                    placeholder={account ? 'Leave empty to keep existing' : 'Enter Access Token'}
                    error={errors.accessToken?.message}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>

            {/* Help Links */}
            <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-orange-400" />
                <span className="text-xs font-medium text-gray-300">Need help finding these values?</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <a 
                  href="https://developers.facebook.com/apps/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300"
                >
                  Meta for Developers <ExternalLink className="w-3 h-3" />
                </a>
                <a 
                  href="https://business.facebook.com/settings/whatsapp-business-accounts" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300"
                >
                  WhatsApp Business Settings <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-800">
              {isEditing && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsEditing(false);
                    reset();
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={!isDirty || saving}
              >
                {saving ? (
                  <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="w-3.5 h-3.5 mr-1.5" /> {account ? 'Update Settings' : 'Connect WhatsApp'}</>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Webhook Configuration */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#121723] rounded-xl border border-gray-800 p-4"
      >
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="w-4 h-4 text-orange-400" />
          <h3 className="text-sm font-heading font-semibold text-white">Webhook Configuration</h3>
        </div>
        
        <p className="text-xs text-(--color-muted-gray) mb-3">
          Configure this URL in your Meta App's WhatsApp webhook settings to receive messages.
        </p>

        <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
          <p className="text-xs text-(--color-muted-gray) mb-1">Webhook URL</p>
          <div className="flex items-center justify-between gap-2">
            <code className="text-sm text-orange-400 font-mono break-all">{webhookUrl}</code>
            <CopyButton text={webhookUrl} />
          </div>
        </div>

        {account && (
          <div className="mt-3 p-3 bg-[#0D1117] rounded-lg border border-gray-800">
            <p className="text-xs text-(--color-muted-gray) mb-1">Verify Token</p>
            <div className="flex items-center justify-between gap-2">
              <code className="text-sm text-white font-mono truncate min-w-0 flex-1">
                {showSecrets ? account.webhookVerifyToken : '••••••••••••'}
              </code>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => setShowSecrets(!showSecrets)}
                  className="p-1.5 rounded-md hover:bg-gray-700 transition-colors text-gray-400 hover:text-white"
                >
                  {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {account && <CopyButton text={account.webhookVerifyToken} />}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Disconnect Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDisconnectDialog}
        onClose={() => setShowDisconnectDialog(false)}
        onConfirm={handleDisconnectConfirm}
        variant="danger"
        title="Disconnect WhatsApp?"
        description={
          <>
            Are you sure you want to disconnect your WhatsApp account?
            <br />
            <span className="text-red-400 font-medium">This will stop all messaging functionality.</span>
          </>
        }
        confirmText="Yes, Disconnect"
        cancelText="Keep Connected"
        isLoading={isDisconnecting}
      />

      {/* Multiple organizations: add a WhatsApp account, buying more first when none is left */}
      {addAccountModals}
    </div>
  );
};

export default WhatsAppSettingsPage;

